import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Font } from '@react-pdf/renderer';

// Fontları kaydet
Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf', fontWeight: 300 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 400 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf', fontWeight: 500 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 700 },
  ]
});

// Stilleri oluştur
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: 30,
    fontFamily: 'Roboto',
  },
  header: {
    fontSize: 24,
    marginBottom: 20,
    fontWeight: 700,
    textAlign: 'center',
    color: '#3f51b5',
  },
  subheader: {
    fontSize: 14,
    marginBottom: 10,
    color: '#757575',
  },
  statsContainer: {
    flexDirection: 'row',
    marginBottom: 20,
    justifyContent: 'space-between',
  },
  statBox: {
    padding: 10,
    width: '23%',
    backgroundColor: '#f5f5f5',
    borderRadius: 5,
  },
  statLabel: {
    fontSize: 10,
    color: '#757575',
  },
  statValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#333',
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bdbdbd',
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#bdbdbd',
  },
  tableRowHeader: {
    backgroundColor: '#f5f5f5',
  },
  tableCol: {
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: '#bdbdbd',
  },
  idCol: { width: '8%' },
  priorityCol: { width: '12%' },
  messageCol: { width: '25%' },
  tagNameCol: { width: '15%' },
  dateCol: { width: '20%' },
  statusCol: { width: '20%' },
  tableCell: {
    fontSize: 10,
  },
  tableCellHeader: {
    fontSize: 10,
    fontWeight: 700,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 10,
    color: '#9e9e9e',
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    fontSize: 10,
    color: '#9e9e9e',
  },
  highPriority: {
    color: '#d32f2f',
    fontWeight: 500,
  },
  mediumPriority: {
    color: '#f57c00',
    fontWeight: 500,
  },
  lowPriority: {
    color: '#0288d1',
    fontWeight: 500,
  },
  confirmed: {
    color: '#388e3c',
  },
  unconfirmed: {
    color: '#f57c00',
  },
});

// Yardımcı fonksiyonlar
const formatDate = (dateString) => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "Geçersiz Tarih";
  }

  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const seconds = ("0" + date.getSeconds()).slice(-2);

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const getPriorityStyle = (priority) => {
  switch (priority) {
    case "high": return styles.highPriority;
    case "medium": return styles.mediumPriority;
    case "low": return styles.lowPriority;
    default: return {};
  }
};

const getPriorityText = (priority) => {
  switch (priority) {
    case "high": return "Yüksek";
    case "medium": return "Orta";
    case "low": return "Düşük";
    default: return "Belirsiz";
  }
};

const AlarmPDFDocument = ({ alarms }) => {
  // İstatistikleri hesapla
  const stats = {
    total: alarms.length,
    unconfirmed: alarms.filter(a => a.confirm_ !== 1).length,
    critical: alarms.filter(a => a.priority === "high").length,
    warning: alarms.filter(a => a.priority === "medium").length,
  };

  // Şu anki tarihi formatla
  const currentDate = new Date();
  const formattedCurrentDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>Alarm Raporu</Text>
        <Text style={styles.subheader}>Rapor Tarihi: {formattedCurrentDate}</Text>
        
        {/* İstatistik Kutuları */}
        <View style={styles.statsContainer}>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>Toplam</Text>
            <Text style={styles.statValue}>{stats.total}</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>Onaylanmamış</Text>
            <Text style={styles.statValue}>{stats.unconfirmed}</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>Kritik</Text>
            <Text style={styles.statValue}>{stats.critical}</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statLabel}>Uyarı</Text>
            <Text style={styles.statValue}>{stats.warning}</Text>
          </View>
        </View>
        
        {/* Alarm Tablosu */}
        <View style={styles.table}>
          {/* Tablo Başlık Satırı */}
          <View style={[styles.tableRow, styles.tableRowHeader]}>
            <View style={[styles.tableCol, styles.idCol]}>
              <Text style={styles.tableCellHeader}>ID</Text>
            </View>
            <View style={[styles.tableCol, styles.priorityCol]}>
              <Text style={styles.tableCellHeader}>Öncelik</Text>
            </View>
            <View style={[styles.tableCol, styles.messageCol]}>
              <Text style={styles.tableCellHeader}>Mesaj</Text>
            </View>
            <View style={[styles.tableCol, styles.tagNameCol]}>
              <Text style={styles.tableCellHeader}>Etiket</Text>
            </View>
            <View style={[styles.tableCol, styles.dateCol]}>
              <Text style={styles.tableCellHeader}>Tarih</Text>
            </View>
            <View style={[styles.tableCol, styles.statusCol]}>
              <Text style={styles.tableCellHeader}>Durum</Text>
            </View>
          </View>
          
          {/* Veri Satırları */}
          {alarms.map((alarm) => (
            <View key={alarm.id} style={styles.tableRow}>
              <View style={[styles.tableCol, styles.idCol]}>
                <Text style={styles.tableCell}>{alarm.id}</Text>
              </View>
              <View style={[styles.tableCol, styles.priorityCol]}>
                <Text style={[styles.tableCell, getPriorityStyle(alarm.priority)]}>
                  {getPriorityText(alarm.priority)}
                </Text>
              </View>
              <View style={[styles.tableCol, styles.messageCol]}>
                <Text style={styles.tableCell}>{alarm.message}</Text>
              </View>
              <View style={[styles.tableCol, styles.tagNameCol]}>
                <Text style={styles.tableCell}>{alarm.tagName}</Text>
              </View>
              <View style={[styles.tableCol, styles.dateCol]}>
                <Text style={styles.tableCell}>{formatDate(alarm.date)}</Text>
              </View>
              <View style={[styles.tableCol, styles.statusCol]}>
                <Text 
                  style={[
                    styles.tableCell, 
                    alarm.confirm_ === 1 ? styles.confirmed : styles.unconfirmed
                  ]}
                >
                  {alarm.confirm_ === 1 ? "Onaylandı" : "Onaylanmadı"}
                </Text>
              </View>
            </View>
          ))}
        </View>
        
        {/* Altbilgi */}
        <Text style={styles.footer}>
          Bu rapor otomatik olarak oluşturulmuştur.
        </Text>
        <Text 
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => (
            `${pageNumber} / ${totalPages}`
          )} 
        />
      </Page>
    </Document>
  );
};

export default AlarmPDFDocument;